// General
import "./profile-verification.scss";
import { useEffect } from "react";
// Services
import {
  useLazyGetProfileQuery,
  useRequestVp3Mutation,
} from "../../../../../services/data.service";
// Static Data
import routeConst from "../../../../../const/routeConst";
// Redux
import { useSelector, useDispatch } from "react-redux";
import { updateVp3ExchangeStatus } from "../../../../../redux/store/userCardStore";
import {
  updateInfoToast,
  updateErrorToast,
} from "../../../../../redux/store/toastStore";
import { updateProfilePreviewVerifiedPhotoInfo } from "../../../../../redux/store/profileStore";
import { updatePreviewVerifiedPhotoDialog } from "../../../../../redux/store/dialogStore";
// Material UI
import { ButtonBase } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
// i18next
import { useTranslation } from "react-i18next";
// Custom Hooks
import IconManager from "../../../../utility/manager/icon-manager/icon-manager";
import useCustomNavigate from "../../../../utility/custom-hooks/useCustomNavigate-hook";
// Components
import CustomAvatar from "../../custom-avatar/custom-avatar";

const ProfileVerification = () => {
  // API variables
  const [
    getProfile,
    {
      data: getProfileData,
      error: getProfileErrorData,
      isFetching: getProfileFetching,
      isLoading: getProfileLoading,
      isSuccess: getProfileSuccess,
      isError: getProfileError,
    },
  ] = useLazyGetProfileQuery();
  const [
    requestVp3,
    {
      data: requestVp3Data,
      error: requestVp3ErrorData,
      isLoading: requestVp3Loading,
      isSuccess: requestVp3Success,
      isError: requestVp3Error,
    },
  ] = useRequestVp3Mutation();

  // Redux variables
  const id = useSelector((state) => state.userCard.id);
  const idInt = useSelector((state) => state.userCard.idInt);
  const username = useSelector((state) => state.userCard.username);
  const vp3Photo = useSelector((state) => state.userCard.vp3Photo);
  const vp3Verified = useSelector((state) => state.userCard.vp3Verified);
  const vp3ExchangeStatus = useSelector(
    (state) => state.userCard.vp3ExchangeStatus
  );
  const dispatch = useDispatch();

  // i18next variables
  const { t } = useTranslation();

  // Lifecycle | Mounted
  useEffect(() => {
    getProfile(null, true);
  }, []);

  // Lifecycle | Check for update | Request VP3 API Response
  useEffect(() => {
    if (requestVp3Loading) {
    } else if (requestVp3Success) {
      switch (requestVp3Data?.status) {
        case 1:
          dispatch(
            updateVp3ExchangeStatus(requestVp3Data?.data?.request?.status)
          );
          break;
        default:
          const obj = {
            message: requestVp3Data?.message,
            autoClose: 4000,
          };
          dispatch(updateInfoToast(obj));
          break;
      }
    } else if (requestVp3Error) {
      if (requestVp3ErrorData?.status === 401) {
        onNavigate(routeConst.logout.path);
      } else {
        const obj = {
          message: requestVp3ErrorData?.data?.message,
          autoClose: 4000,
        };
        dispatch(updateErrorToast(obj));
      }
    }
  }, [requestVp3Loading, requestVp3Success, requestVp3Error]);

  // Custom Hooks Functions
  const getIcon = IconManager();
  const onNavigate = useCustomNavigate();

  // Event Handlers | Button
  const onVerify = () => {
    onNavigate(routeConst.verify.sugarbookVerified.onboarding.path);
  };
  const onRequest = () => {
    if (!id) return;

    const obj = {
      user_id: id,
    };
    requestVp3(obj);
  };
  const onViewVp3 = () => {
    const obj = {
      photoUrl: vp3Photo?.asset_url,
      showUploadVpButton: false,
      showVpLabel: true,
    };

    dispatch(updateProfilePreviewVerifiedPhotoInfo(obj));
    dispatch(updatePreviewVerifiedPhotoDialog(true));
  };

  if (!vp3Verified) return;

  return (
    <div id="profile-verification-subcomponent">
      <div className="padding-container">
        <div className="profile-verification-label">
          {t("profile.verification")}
        </div>

        <div style={{ padding: "5px" }} />

        <div className="profile-verification-body">
          <div className="profile-verification-description">
            <CustomAvatar src={vp3Photo?.asset_url} />

            <div className="profile-verification-body-label">
              {t(
                idInt === getProfileData?.data?.id_int
                  ? "profile.own_photo_verified"
                  : "profile.other_photo_verified",
                { username: username }
              )}

              {idInt !== getProfileData?.data?.id_int &&
                !getProfileData?.data?.vp3?.verified && (
                  <div
                    className="profile-verification-button"
                    onClick={onVerify}
                  >
                    <ButtonBase className="profile-verification-button-label">
                      {t("profile.verification_cta_3")}
                    </ButtonBase>
                    <ChevronRightIcon className="right-icon" />
                  </div>
                )}

              {idInt !== getProfileData?.data?.id_int &&
                getProfileData?.data?.vp3?.verified &&
                !vp3ExchangeStatus && (
                  <div
                    className="profile-verification-button"
                    onClick={onRequest}
                  >
                    <ButtonBase className="profile-verification-button-label">
                      {t("profile.verification_cta_2")}
                    </ButtonBase>
                    <ChevronRightIcon className="right-icon" />
                  </div>
                )}

              {idInt !== getProfileData?.data?.id_int &&
                getProfileData?.data?.vp3?.verified &&
                vp3ExchangeStatus === "approved" && (
                  <div
                    className="profile-verification-button"
                    onClick={onViewVp3}
                  >
                    <ButtonBase className="profile-verification-button-label">
                      {t("profile.verification_cta")}
                    </ButtonBase>
                    <ChevronRightIcon className="right-icon" />
                  </div>
                )}

              {idInt !== getProfileData?.data?.id_int &&
                getProfileData?.data?.vp3?.verified &&
                vp3ExchangeStatus === "pending" && (
                  <div className="profile-verification-button">
                    <ButtonBase className="profile-verification-button-disabled">
                      {t("profile.private_requested")}
                    </ButtonBase>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileVerification;
